// analyticsSchema.ts — Reads a customer's plan data through a configurable
// mapping instead of hard-coded field names. See src/types/analytics.ts for the
// why and for the config shape.

import { SpaceInfo, TenantInfo } from "../../../types/plan";
import {
  AnalyticsFieldMap,
  AnalyticsFieldRole,
  AnalyticsSchemaConfig,
  WingAnalysisColumn,
  WingAnalysisConfig,
} from "../../../types/analytics";

// ─── Value parsing ───────────────────────────────────────────────────────────

/**
 * Reduce "1,234.56" / "1.234,56" / "16,87" / "1234" to a JS-parseable string.
 *
 * When both separators appear, the right-most one is the decimal mark. When only
 * one appears it is a thousands separator if — and only if — it groups digits in
 * threes all the way to the end ("255,193"); otherwise it is a decimal mark
 * ("16,87"). A lone "1,000" is irreducibly ambiguous and is read as thousands,
 * which is what the previous implementation did.
 */
function normalizeSeparators(digits: string): string {
  const lastDot = digits.lastIndexOf(".");
  const lastComma = digits.lastIndexOf(",");

  if (lastDot >= 0 && lastComma >= 0) {
    const decimalMark = lastDot > lastComma ? "." : ",";
    const thousandsMark = decimalMark === "." ? "," : ".";
    return digits.split(thousandsMark).join("").replace(decimalMark, ".");
  }

  const mark = lastDot >= 0 ? "." : lastComma >= 0 ? "," : "";
  if (!mark) return digits;

  const escaped = mark === "." ? "\\." : ",";
  if (new RegExp(`^\\d{1,3}(${escaped}\\d{3})+$`).test(digits)) {
    return digits.split(mark).join("");
  }
  return digits.replace(mark, ".");
}

/**
 * Parse a number out of a data-sheet value.
 *
 * Customer datasets ship the same figure in many skins — "$255,193", "€255193",
 * "195.7 m²", "16.87%", "1.234,56", "(1,234)" — so everything that is not part
 * of the number is stripped, rather than whitelisting the two symbols we
 * happened to have seen. The previous version only removed "$" and ",", which
 * silently turned every euro/metric value into 0 and was the direct cause of the
 * all-zeros Wing Analysis on metric demo data.
 */
export function parseNumber(value: string | number | undefined | null): number {
  if (value === undefined || value === null || value === "") return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;

  const raw = String(value).trim();
  if (!raw) return 0;

  // Accounting negative: "(1,234)" means -1234.
  const parenthesized = /^\(.*\)$/.test(raw);
  const body = parenthesized ? raw.slice(1, -1) : raw;

  // Join thousands written with a (possibly non-breaking) space: "1 234" → "1234".
  const joined = body.replace(/(\d)[\s  ](?=\d{3}(\D|$))/g, "$1");

  // Take the FIRST numeric run, rather than every digit in the string: units are
  // routinely written with a digit in them ("195.7 m2", "1 250 sq ft"), and
  // stripping non-digits wholesale glued that digit onto the value — "100 m2"
  // parsed as 1002.
  const match = joined.match(/\d[\d.,]*/);
  if (!match) return 0;

  const token = match[0];
  // The minus sign may sit before the currency symbol ("-$500"), so it is read
  // from what precedes the number rather than from the number itself.
  const prefix = joined.slice(0, match.index ?? 0);
  const negative = parenthesized || /-\s*[^0-9A-Za-z]*$/.test(prefix);
  // A trailing separator is punctuation, not part of the number ("1,234." ).
  const digits = token.replace(/^[+-]/, "").replace(/[.,]+$/, "");
  const parsed = parseFloat(normalizeSeparators(digits));
  if (Number.isNaN(parsed)) return 0;
  return negative ? -parsed : parsed;
}

// ─── Defaults ────────────────────────────────────────────────────────────────

/**
 * Default candidate keys per role. These reproduce exactly the keys that were
 * hard-coded in the calculations, so an existing deployment that ships no
 * `analyticsSchema` behaves as before.
 */
export const DEFAULT_ANALYTICS_FIELDS: Record<AnalyticsFieldRole, string[]> = {
  tenantName: ["DBA", "Employee", "Occupant"],
  area: [
    "GLA",
    "Actual Footprint SF",
    "Actual Footprint M2",
    "Actual Footprint",
    "Square Footage",
    "Area",
  ],
  sales: ["Sales R12"],
  salesPerArea: ["Sales R12 PSF", "Sales R12 /M2", "Sales R12 per m2"],
  rent: ["Total Rent", "Gross Rent"],
  rentPerArea: [
    "Total Rent PSF",
    "Gross Rent PSF",
    "Total Rent /M2",
    "Total Rent per m2",
  ],
  occCostPerArea: [
    "Occupancy Cost PSF",
    "Occupancy Cost /M2",
    "Occupancy Cost per m2",
  ],
  occCostPct: ["Occupancy Cost %", "Occupancy Cost"],
  leaseStart: [
    "Lease Start Date",
    "Projected Rent Commencement Date",
    "Date of Possession (Projected Per TC)",
    "Date of Possession (Mutually Agreed)",
  ],
  leaseEnd: ["Lease End Date"],
  rentExpiration: ["Rent Expiration Date"],
  dealStage: ["Deal Stage"],
  dealName: ["Deal Name"],
  category: ["Merchandise Category", "Department", "Company"],
};

export interface ResolvedAnalyticsSchema {
  tenantTabMatch: string[];
  futureTabMatch: string[];
  tenantTabExclude: string[];
  fallbackToFirstTab: boolean;
  areaUnitLabel: string;
  currencyLabel: string;
  dateOrder: "auto" | "MDY" | "DMY";
  fields: Record<AnalyticsFieldRole, string[]>;
}

const DEFAULT_SCHEMA: ResolvedAnalyticsSchema = {
  // "employee information" is included by default: an occupancy plan carries no
  // tenant tab at all, so without it every corporate dataset resolved to nothing.
  tenantTabMatch: ["tenant information", "employee information", "occupant information"],
  futureTabMatch: ["future tenant information"],
  tenantTabExclude: ["future"],
  fallbackToFirstTab: false,
  areaUnitLabel: "SF",
  currencyLabel: "$",
  dateOrder: "auto",
  fields: DEFAULT_ANALYTICS_FIELDS,
};

/** Case-insensitive lookup of a per-planType override. */
function planTypeOverride<T>(
  byPlanType: Record<string, T> | undefined,
  planType: string | undefined
): T | undefined {
  if (!byPlanType || !planType) return undefined;
  const wanted = planType.toLowerCase().trim();
  const key = Object.keys(byPlanType).find((k) => k.toLowerCase().trim() === wanted);
  return key === undefined ? undefined : byPlanType[key];
}

/**
 * Merge a customer config over the defaults, then the `planType` override over
 * that. Field roles are merged *per role*: a role the customer defines replaces
 * its default candidate list entirely, while roles they leave out keep the
 * defaults. That per-role merge also makes the mapping survive the shallow
 * `jq '+'` merge the deployment workflow applies to config_overrides — a partial
 * `fields` object loses nothing.
 */
export function resolveAnalyticsSchema(
  baseConfig?: AnalyticsSchemaConfig,
  planType?: string
): ResolvedAnalyticsSchema {
  const override = planTypeOverride(baseConfig?.byPlanType, planType);
  const config: AnalyticsSchemaConfig | undefined =
    baseConfig && override
      ? {
          ...baseConfig,
          ...override,
          fields: { ...(baseConfig.fields || {}), ...(override.fields || {}) },
        }
      : baseConfig;

  if (!config) return DEFAULT_SCHEMA;

  const fields = { ...DEFAULT_ANALYTICS_FIELDS };
  const overrides: AnalyticsFieldMap = config.fields || {};
  (Object.keys(overrides) as AnalyticsFieldRole[]).forEach((role) => {
    const list = overrides[role];
    // An explicitly empty list disables the role (nothing will resolve).
    if (Array.isArray(list)) fields[role] = list;
  });

  const lower = (values: string[] | undefined) =>
    values?.map((v) => v.toLowerCase().trim()).filter(Boolean);

  return {
    tenantTabMatch: lower(config.tenantTabMatch) ?? DEFAULT_SCHEMA.tenantTabMatch,
    futureTabMatch: lower(config.futureTabMatch) ?? DEFAULT_SCHEMA.futureTabMatch,
    tenantTabExclude:
      lower(config.tenantTabExclude) ?? DEFAULT_SCHEMA.tenantTabExclude,
    fallbackToFirstTab: config.fallbackToFirstTab ?? false,
    areaUnitLabel: config.areaUnitLabel ?? DEFAULT_SCHEMA.areaUnitLabel,
    currencyLabel: config.currencyLabel ?? DEFAULT_SCHEMA.currencyLabel,
    dateOrder: config.dateOrder ?? DEFAULT_SCHEMA.dateOrder,
    fields,
  };
}

// ─── Tab resolution ──────────────────────────────────────────────────────────

export interface OccupantTab {
  detail: TenantInfo | null;
  caption: string;
  /** True when a tab matched the configured captions. */
  matched: boolean;
  /** True when `fallbackToFirstTab` had to be used (the mapping is a guess). */
  usedFallback: boolean;
}

/**
 * Find the detail tab holding the current (or future) occupant's record.
 *
 * The caption patterns are configurable because the tab is named after the
 * business domain: "Tenant Information" on a lease plan, "Employee Information"
 * on a corporate plan. Previously the literal "tenant information" was required,
 * so every non-retail dataset resolved to null and produced a table of zeros.
 */
export function resolveOccupantTab(
  space: SpaceInfo,
  schema: ResolvedAnalyticsSchema,
  kind: "current" | "future" = "current"
): OccupantTab {
  const tabs = space.detailTabs || [];
  const patterns = kind === "future" ? schema.futureTabMatch : schema.tenantTabMatch;

  const tab = tabs.find((t) => {
    const caption = (t.caption || "").toLowerCase();
    const hit = patterns.some((p) => caption.includes(p));
    if (!hit) return false;
    // The "current" tab must not be the future one; "future" has no exclusion.
    if (kind === "future") return true;
    return !schema.tenantTabExclude.some((x) => caption.includes(x));
  });

  if (tab) {
    return { detail: tab.detail || null, caption: tab.caption, matched: true, usedFallback: false };
  }

  // No fallback for the future occupant: guessing there would count the current
  // tenant's rent twice in the Opportunity Cost.
  if (kind === "current" && schema.fallbackToFirstTab && tabs.length > 0) {
    return {
      detail: tabs[0].detail || null,
      caption: tabs[0].caption,
      matched: false,
      usedFallback: true,
    };
  }

  return { detail: null, caption: "", matched: false, usedFallback: false };
}

// ─── Field resolution ────────────────────────────────────────────────────────

function isBlank(value: string | number | undefined): boolean {
  return value === undefined || value === null || String(value).trim() === "";
}

export interface ResolvedField {
  /** The source key that carried the value. */
  key: string;
  value: string | number;
}

/**
 * Resolve one logical role against an occupant record.
 *
 * Preference order mirrors the old `a || b` chains: a candidate that is present
 * but blank or zero is skipped in favour of a later candidate that carries a
 * real figure. If every present candidate is zero we still return the first one,
 * so callers can tell "the dataset says 0" from "the field does not exist".
 */
export function findField(
  detail: TenantInfo | null | undefined,
  role: AnalyticsFieldRole,
  schema: ResolvedAnalyticsSchema
): ResolvedField | null {
  if (!detail) return null;
  const candidates = schema.fields[role] || [];

  let firstPresent: ResolvedField | null = null;
  for (const key of candidates) {
    const value = detail[key];
    if (isBlank(value)) continue;
    const resolved: ResolvedField = { key, value: value as string | number };
    if (!firstPresent) firstPresent = resolved;
    // A non-numeric string (a name, a date, a category) is always meaningful.
    if (typeof value === "string" && !/\d/.test(value)) return resolved;
    if (parseNumber(value) !== 0) return resolved;
  }
  return firstPresent;
}

/** Numeric value of a role, 0 when unresolved. */
export function fieldNumber(
  detail: TenantInfo | null | undefined,
  role: AnalyticsFieldRole,
  schema: ResolvedAnalyticsSchema
): number {
  return parseNumber(findField(detail, role, schema)?.value);
}

/** Text value of a role, "" when unresolved. */
export function fieldText(
  detail: TenantInfo | null | undefined,
  role: AnalyticsFieldRole,
  schema: ResolvedAnalyticsSchema
): string {
  const found = findField(detail, role, schema);
  return found === null ? "" : String(found.value).trim();
}

/** True when the record carries no key at all for this role. */
export function hasField(
  detail: TenantInfo | null | undefined,
  role: AnalyticsFieldRole,
  schema: ResolvedAnalyticsSchema
): boolean {
  return findField(detail, role, schema) !== null;
}

/**
 * Roles that resolved to nothing across every selected space. Used to tell the
 * user their mapping is wrong instead of showing a table of zeros.
 */
export function unresolvedRoles(
  details: (TenantInfo | null)[],
  roles: AnalyticsFieldRole[],
  schema: ResolvedAnalyticsSchema
): AnalyticsFieldRole[] {
  return roles.filter(
    (role) => !details.some((detail) => hasField(detail, role, schema))
  );
}

/** Human-readable "role (tried: key A, key B)" used in warning messages. */
export function describeRole(
  role: AnalyticsFieldRole,
  schema: ResolvedAnalyticsSchema
): string {
  const tried = schema.fields[role] || [];
  return tried.length ? `${role} (tried: ${tried.join(", ")})` : `${role} (no keys configured)`;
}

// ─── Wing Analysis columns ───────────────────────────────────────────────────

/**
 * Built-in Wing Analysis columns: the retail deal-sheet layout this feature was
 * designed around. A customer whose dataset has no sales figures (corporate,
 * healthcare) configures their own columns instead.
 */
export function defaultWingColumns(
  schema: ResolvedAnalyticsSchema
): WingAnalysisColumn[] {
  const area = schema.areaUnitLabel;
  const per = area === "SF" ? "PSF" : `/${area}`;
  return [
    { key: "space_number", label: "Space #", source: "spaceNumber", type: "text", aggregate: "none" },
    { key: "tenant_name", label: "Tenant", source: "field:tenantName", type: "text", aggregate: "none" },
    { key: "gla_sf", label: `GLA (${area})`, source: "field:area", type: "area", aggregate: "avg" },
    { key: "sales_psf", label: `Sales ${per}`, source: "field:salesPerArea", type: "money", aggregate: "weightedByArea" },
    { key: "occ_cost_psf", label: `Occ Cost ${per}`, source: "field:occCostPerArea", type: "money", aggregate: "weightedByArea" },
    { key: "occ_cost_percent", label: "Occ Cost %", source: "field:occCostPct", type: "percentage", aggregate: "weightedBySales" },
  ];
}

// ─── Derived metrics ─────────────────────────────────────────────────────────

/**
 * The figures every analytics table needs, with the cross-derivations that make
 * a partial dataset usable:
 *  - `salesPerArea` / `rentPerArea` are derived from the absolute figure ÷ area
 *    when the dataset ships no per-area column under a mapped key;
 *  - occupancy cost is derived in whichever direction is available — from the
 *    per-area figure (× area) or from the percentage (× sales) — instead of the
 *    old one-way `Occupancy Cost PSF × area`, which mislabelled itself as a
 *    percentage conversion and yielded 0 whenever only the percentage existed.
 */
export interface OccupantMetrics {
  area: number;
  sales: number;
  salesPerArea: number;
  rent: number;
  rentPerArea: number;
  occCostPerArea: number;
  occCostPct: number;
  /** Absolute occupancy cost, derived. Drives the weighted roll-ups. */
  occCostAbsolute: number;
}

export function readOccupantMetrics(
  detail: TenantInfo | null,
  schema: ResolvedAnalyticsSchema
): OccupantMetrics {
  const area = fieldNumber(detail, "area", schema);
  const sales = fieldNumber(detail, "sales", schema);
  const rent = fieldNumber(detail, "rent", schema);

  const salesPerArea =
    fieldNumber(detail, "salesPerArea", schema) || (area > 0 ? sales / area : 0);
  const rentPerArea =
    fieldNumber(detail, "rentPerArea", schema) || (area > 0 ? rent / area : 0);

  let occCostPerArea = fieldNumber(detail, "occCostPerArea", schema);
  let occCostPct = fieldNumber(detail, "occCostPct", schema);
  let occCostAbsolute = occCostPerArea * area;

  if (occCostAbsolute === 0 && occCostPct > 0 && sales > 0) {
    occCostAbsolute = (sales * occCostPct) / 100;
    if (area > 0) occCostPerArea = occCostAbsolute / area;
  } else if (occCostAbsolute > 0 && occCostPct === 0 && sales > 0) {
    occCostPct = (occCostAbsolute / sales) * 100;
  }

  return {
    area,
    sales,
    salesPerArea,
    rent,
    rentPerArea,
    occCostPerArea,
    occCostPct,
    occCostAbsolute,
  };
}

/** Apply the `planType` override to the Wing Analysis table config. */
export function resolveWingConfig(
  baseConfig: WingAnalysisConfig | undefined,
  planType?: string
): WingAnalysisConfig {
  if (!baseConfig) return {};
  const override = planTypeOverride(baseConfig.byPlanType, planType);
  return override ? { ...baseConfig, ...override } : baseConfig;
}

/** `field:area` → "area"; anything else → null. */
export function columnRole(column: WingAnalysisColumn): AnalyticsFieldRole | null {
  if (typeof column.source !== "string") return null;
  const match = /^field:(.+)$/.exec(column.source);
  return match ? (match[1].trim() as AnalyticsFieldRole) : null;
}
